import type { InvestorSortField } from "@/domain/investor";
import type { IInvestorRepository } from "@/application/ports/repositories";
import type {
  CompanyInvestor,
  CreateCompanyInvestorRequest,
  CreateIndividualInvestorRequest,
  IndividualInvestor,
  InvestorListParams,
  InvestorExportParams,
  InvestorDocumentType,
  InvestorLegalRepCreateRequest,
  InvestorLegalRepresentative,
  InvestorLegalRepUpdateRequest,
  InvestorListResponse,
  InvestorMetrics,
  InvestorMusicDeductionSummary,
  InvestorOperationResponse,
  InvestorStatus,
  InvestorType,
  UpdateCompanyInvestorRequest,
  UpdateIndividualInvestorRequest,
} from "@/domain/investor";
import type { OrganizationInvitation } from "@/domain/invitation";
import type {
  DocumentWithReviewResponse,
  DocumentDownloadResponse,
  InvestorPortfolioResponse,
  ReviewUpdateRequest,
  DocumentUpdateRequest,
} from "@/domain/documentBlocks";
import type { ExportDownloadOptions, ExportPreview } from "@/domain/export";
import type { Language } from "@/domain/common";
import type { DocumentStatus, DocumentType } from "@/domain/documentEnums";
import { ApiClientError } from "@/application/errors/ApiClientError";
import { downloadBlob } from "../fileDownload";
import type { CompanyResponse, IndividualResponse } from "../generated/types.gen";
import * as sdk from "../generated/sdk.gen";
import { unwrap, unwrapBlobWithFilename } from "../unwrap";

function toLanguage(value: string): Language {
  return value === "en" || value === "ca" ? value : "es";
}

function mapIndividual(investor: IndividualResponse): IndividualInvestor {
  return { ...investor, communication_language: toLanguage(investor.communication_language) };
}

function mapCompany(investor: CompanyResponse): CompanyInvestor {
  return { ...investor, communication_language: toLanguage(investor.communication_language) };
}

export class InvestorRepository implements IInvestorRepository {
  async list(params: InvestorListParams = {}, signal?: AbortSignal): Promise<InvestorListResponse> {
    const { items, ...rest } = await unwrap(sdk.listInvestors({ query: params, signal }));
    return { investors: items, ...rest };
  }

  async getMetrics(params?: {
    status?: InvestorStatus;
    investor_type?: InvestorType;
    search?: string;
  }): Promise<InvestorMetrics> {
    return unwrap(sdk.getInvestorMetrics({ query: params }));
  }

  async getMusicDeductionSummary(
    uuid: string,
    fiscalYear: number,
  ): Promise<InvestorMusicDeductionSummary> {
    return unwrap(
      sdk.getInvestorMusicDeductionSummary({
        path: { investor_uuid: uuid },
        query: { fiscal_year: fiscalYear },
      }),
    );
  }

  async createIndividual(data: CreateIndividualInvestorRequest): Promise<IndividualInvestor> {
    return mapIndividual(await unwrap(sdk.createIndividualInvestor({ body: data })));
  }

  async getIndividual(uuid: string): Promise<IndividualInvestor> {
    return mapIndividual(
      await unwrap(sdk.getIndividualInvestor({ path: { investor_uuid: uuid } })),
    );
  }

  async updateIndividual(
    uuid: string,
    data: UpdateIndividualInvestorRequest,
  ): Promise<IndividualInvestor> {
    return mapIndividual(
      await unwrap(sdk.updateIndividualInvestor({ path: { investor_uuid: uuid }, body: data })),
    );
  }

  async createCompany(data: CreateCompanyInvestorRequest): Promise<CompanyInvestor> {
    const { legal_representatives: legalRepresentatives, ...body } = data;
    const investor = mapCompany(await unwrap(sdk.createCompanyInvestor({ body })));

    for (const representative of legalRepresentatives ?? []) {
      await this.createLegalRepresentative(investor.uuid, representative);
    }

    return legalRepresentatives?.length ? this.getCompany(investor.uuid) : investor;
  }

  async getCompany(uuid: string): Promise<CompanyInvestor> {
    return mapCompany(await unwrap(sdk.getCompanyInvestor({ path: { investor_uuid: uuid } })));
  }

  async updateCompany(uuid: string, data: UpdateCompanyInvestorRequest): Promise<CompanyInvestor> {
    return mapCompany(
      await unwrap(sdk.updateCompanyInvestor({ path: { investor_uuid: uuid }, body: data })),
    );
  }

  // --- Legal Representatives ---

  async listLegalRepresentatives(investorUuid: string): Promise<InvestorLegalRepresentative[]> {
    const { items } = await unwrap(
      sdk.listInvestorLegalRepresentatives({ path: { investor_uuid: investorUuid } }),
    );
    return items;
  }

  async createLegalRepresentative(
    investorUuid: string,
    data: InvestorLegalRepCreateRequest,
  ): Promise<InvestorLegalRepresentative> {
    return unwrap(
      sdk.createInvestorLegalRepresentative({
        path: { investor_uuid: investorUuid },
        body: data,
      }),
    );
  }

  async updateLegalRepresentative(
    investorUuid: string,
    representativeUuid: string,
    data: InvestorLegalRepUpdateRequest,
  ): Promise<void> {
    await unwrap(
      sdk.updateInvestorLegalRepresentative({
        path: { investor_uuid: investorUuid, representative_uuid: representativeUuid },
        body: data,
      }),
    );
  }

  async setPrimaryLegalRepresentative(
    investorUuid: string,
    representativeUuid: string,
  ): Promise<void> {
    await unwrap(
      sdk.setPrimaryInvestorLegalRepresentative({
        path: { investor_uuid: investorUuid, representative_uuid: representativeUuid },
      }),
    );
  }

  async deleteLegalRepresentative(investorUuid: string, representativeUuid: string): Promise<void> {
    await unwrap(
      sdk.deleteInvestorLegalRepresentative({
        path: { investor_uuid: investorUuid, representative_uuid: representativeUuid },
      }),
    );
  }

  async delete(uuid: string): Promise<void> {
    await unwrap(sdk.deleteInvestor({ path: { investor_uuid: uuid } }));
  }

  async activate(uuid: string): Promise<InvestorOperationResponse> {
    return unwrap(sdk.activateInvestor({ path: { investor_uuid: uuid } }));
  }

  async deactivate(uuid: string): Promise<InvestorOperationResponse> {
    return unwrap(sdk.deactivateInvestor({ path: { investor_uuid: uuid } }));
  }

  async inviteToHub(uuid: string, lang: string = "es"): Promise<OrganizationInvitation> {
    return unwrap(sdk.inviteInvestorToHub({ path: { investor_uuid: uuid }, query: { lang } }));
  }

  async listDocuments(
    investorUuid: string,
    params?: {
      doc_type?: DocumentType;
      status?: DocumentStatus;
      fiscal_quota_uuid?: string;
      search?: string;
      limit?: number;
      offset?: number;
    },
  ): Promise<InvestorPortfolioResponse> {
    return unwrap(
      sdk.listInvestorDocuments({ path: { investor_uuid: investorUuid }, query: params }),
    );
  }

  async uploadDocument(
    investorUuid: string,
    file: File,
    docType: InvestorDocumentType,
    title?: string,
  ): Promise<DocumentWithReviewResponse> {
    return unwrap(
      sdk.uploadInvestorDocument({
        path: { investor_uuid: investorUuid },
        body: { file, doc_type: docType, title },
      }),
    );
  }

  async getDocument(
    investorUuid: string,
    documentUuid: string,
  ): Promise<DocumentWithReviewResponse> {
    return unwrap(
      sdk.getInvestorDocument({
        path: { investor_uuid: investorUuid, document_uuid: documentUuid },
      }),
    );
  }

  async getDocumentUrl(
    investorUuid: string,
    documentUuid: string,
  ): Promise<DocumentDownloadResponse> {
    return unwrap(
      sdk.downloadInvestorDocument({
        path: { investor_uuid: investorUuid, document_uuid: documentUuid },
      }),
    );
  }

  async downloadDocument(
    investorUuid: string,
    documentUuid: string,
    fileName?: string,
  ): Promise<void> {
    const documentResponse = await this.getDocumentUrl(investorUuid, documentUuid);

    const actualFileName = documentResponse.filename || fileName || `document-${documentUuid}.pdf`;

    const response = await fetch(documentResponse.signed_url, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      headers: { Accept: "application/pdf" },
    });

    if (!response.ok) {
      throw new ApiClientError({
        message: `Failed to download document: ${response.status} ${response.statusText}`,
        code: "DOWNLOAD_ERROR",
        status: response.status,
      });
    }

    const blob = await response.blob();
    downloadBlob(blob, actualFileName);
  }

  async deleteDocument(investorUuid: string, documentUuid: string): Promise<void> {
    await unwrap(
      sdk.deleteInvestorDocument({
        path: { investor_uuid: investorUuid, document_uuid: documentUuid },
      }),
    );
  }

  async reviewDocument(
    investorUuid: string,
    documentUuid: string,
    review: ReviewUpdateRequest,
  ): Promise<DocumentWithReviewResponse> {
    return unwrap(
      sdk.reviewInvestorDocument({
        path: { investor_uuid: investorUuid, document_uuid: documentUuid },
        body: review,
      }),
    );
  }

  async updateDocument(
    investorUuid: string,
    documentUuid: string,
    data: DocumentUpdateRequest,
  ): Promise<DocumentWithReviewResponse> {
    return unwrap(
      sdk.updateInvestorDocument({
        path: { investor_uuid: investorUuid, document_uuid: documentUuid },
        body: data,
      }),
    );
  }

  async previewExport(params: InvestorExportParams): Promise<ExportPreview> {
    return unwrap(sdk.previewInvestorsExport({ query: params }));
  }

  async downloadExport(
    params: InvestorExportParams,
    options: ExportDownloadOptions,
  ): Promise<void> {
    const { blob, filename } = await unwrapBlobWithFilename(
      sdk.exportInvestors({
        query: {
          ...params,
          format: options.format,
          columns: options.columns.length > 0 ? options.columns.join(",") : undefined,
          sort_by: (options.sort_by as InvestorSortField | undefined) ?? params.sort_by,
          sort_direction: options.sort_direction ?? params.sort_direction,
        },
      }),
      `investors.${options.format}`,
    );
    downloadBlob(blob, filename);
  }
}
